import React, { RefObject, useContext, useMemo } from 'react';

/**
 * Element där rubriken för en navigeringsnivå ska visas
 * Första elementet är rubriken och det andra är ett element där en separator ska renderas
 */
export type HeaderReference = [
  RefObject<HTMLLIElement | null>,
  RefObject<HTMLLIElement | null>
];
/**
 * Lista av element där rubriker för varje nivå i breadcrumbslistan ska visas
 * Första elementet i varje par är rubriken och det andra är separatorytan.
 * Separatorn behöver renderas av samma kompnent som rubriken för att vara säker på att de renderas samtidigt.
 */
export type HeaderReferences = Array<HeaderReference>;
export const HEADER = 0;
export const SEPARATOR = 1;
export interface IBreadcrumbsContext {
  /** En lista av containers där det går att rendera en header för varje navigeringsnivå  */
  headerReferences: HeaderReferences;
  /** En lista av kotroller för att navigera från den aktuella navigeringsnivån */
  backButtonReferences: Array<HTMLSpanElement | null>;
  setBackbuttonRefs: React.Dispatch<
    React.SetStateAction<Array<HTMLSpanElement | null>>
  >;
  /** En lista av stegknappar för att navigera mellan olika objekt i den aktuella listan */
  stepperReferences: Array<HTMLDivElement | null>;
  setStepperRefs: React.Dispatch<
    React.SetStateAction<Array<HTMLDivElement | null>>
  >;
  stepperSmallRef: HTMLDivElement | null;
  setStepperSmallRef: (newValue: HTMLDivElement | null) => void;
  menuReference: HTMLDivElement | null;
  setMenuRef: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  /** Den sista nivån som renderas för tillfället */
  topNavLevel?: number;
  /** Den sista nivån som har synligt breadcrumb-innehåll (exkluderar nivåer som visas inline t.ex. vid showDetailsRight) */
  topBreadcrumbNavLevel?: number;
  /** Navigeringsnivån som den aktuella contexten är i */
  currentNavLevel?: number;
  registerActiveNavLevel: (level: number, showInBreadcrumb?: boolean) => void;
  unregisterActiveNavLevel: (level: number, showInBreadcrumb?: boolean) => void;
}

export const BreadcrumbsContext = React.createContext<IBreadcrumbsContext>({
  headerReferences: [],
  backButtonReferences: [],
  setBackbuttonRefs: () => {},
  stepperReferences: [],
  setStepperRefs: () => {},
  stepperSmallRef: null,
  setStepperSmallRef: () => {},
  menuReference: null,
  setMenuRef: () => {},
  registerActiveNavLevel: () => {},
  unregisterActiveNavLevel: () => {},
});

export const BreadcrumbsContextProvider = BreadcrumbsContext.Provider;

export const NestedBreadcrumbsContextProvider: React.FunctionComponent<
  React.PropsWithChildren<{}>
> = props => {
  const parentContext = useContext(BreadcrumbsContext);
  const newContext = useMemo(
    (): IBreadcrumbsContext => ({
      ...parentContext,
      currentNavLevel:
        parentContext.currentNavLevel == null
          ? 0
          : parentContext.currentNavLevel + 1,
    }),
    [parentContext]
  );
  return (
    <BreadcrumbsContextProvider value={newContext}>
      {props.children}
    </BreadcrumbsContextProvider>
  );
};
